// Donde pintar un desplegable propio (calendario, buscadores, multiselects...) para que
// no se salga de pantalla.
//
// Cada uno decidia por su cuenta, y la mayoria ni lo hacia: abrian siempre hacia abajo y
// pegados al borde izquierdo de su boton. El DatePicker solo miraba el hueco vertical, con
// un alto supuesto de 330, y nada del horizontal: un campo de fecha en una columna de la
// derecha, o en un modal estrecho, abria el calendario medio fuera de la ventana.
//
// Dos piezas:
//  - elegirPosicion: la cuenta, pura, sin DOM. Es lo que se prueba.
//  - usePosicionDesplegable: la mide en el navegador al abrir y la rehace al redimensionar
//    o hacer scroll, y devuelve el estilo para el panel.
import { useCallback, useLayoutEffect, useState } from "react";

const MARGEN = 8;        // lo que se deja libre contra el borde de la ventana o del contenedor
const SEPARACION = 4;    // entre el boton y el panel
const ALTO_MIN = 120;    // por debajo de esto no merece la pena apretar el panel: se prefiere el otro lado

// ancla:    { top, bottom, left, right } del boton, en coordenadas de ventana
// panel:    { ancho, alto } que ocupa el panel desplegado
// limites:  { top, bottom, left, right } de la zona en la que tiene que caber (la ventana,
//           recortada por los contenedores con scroll u overflow oculto que lo envuelven)
// Devuelve  { vertical: "abajo" | "arriba", maxAlto, dx }
//   vertical  de que lado del boton se pinta
//   maxAlto   tope de alto: si el panel es mas alto que el hueco, se queda con ese alto y
//             hace scroll por dentro en vez de salirse
//   dx        desplazamiento horizontal sobre "alineado a la izquierda del boton" (0 si cabe)
export function elegirPosicion({ ancla, panel, limites, preferirArriba = false, margen = MARGEN, separacion = SEPARACION, altoMax = Infinity }) {
  const abajo = limites.bottom - ancla.bottom - margen - separacion;
  const arriba = ancla.top - limites.top - margen - separacion;
  // Un panel con su propio tope de alto (una lista de 280px, por ejemplo) no crece aunque haya sitio.
  const alto = Math.min(panel.alto, altoMax);

  let vertical = preferirArriba ? "arriba" : "abajo";
  const caben = lado => (lado === "abajo" ? abajo : arriba) >= alto;
  if (!caben(vertical)) {
    const otro = vertical === "abajo" ? "arriba" : "abajo";
    // Se cambia de lado si el otro cabe, o si al menos da mas sitio que el elegido.
    if (caben(otro) || (otro === "abajo" ? abajo : arriba) > (vertical === "abajo" ? abajo : arriba)) vertical = otro;
  }
  const hueco = Math.max(0, vertical === "abajo" ? abajo : arriba);
  const maxAlto = alto > hueco ? Math.max(hueco, Math.min(alto, ALTO_MIN)) : alto;

  // Horizontal: alineado a la izquierda del boton si cabe; si no, se corre lo justo hacia la
  // izquierda para que el borde derecho quede dentro; y nunca mas alla del borde izquierdo.
  const minDx = (limites.left + margen) - ancla.left;
  const maxDx = (limites.right - margen) - ancla.left - panel.ancho;
  // (Tambien hacia la derecha: si el boton esta medio tapado por el borde izquierdo de un
  // contenedor con scroll, el panel arranca dentro, no en la parte tapada.)
  const dx = maxDx < minDx ? minDx : Math.min(Math.max(0, minDx), maxDx);

  return { vertical, maxAlto: Math.round(maxAlto), dx: Math.round(dx) };
}

// Zona visible para un elemento: la ventana, recortada por cada ancestro que recorte su
// contenido (overflow distinto de visible). Un modal con scroll o un panel con overflow
// oculto son los que dejan a medias un desplegable que "cabe en la ventana".
export function limitesVisibles(el, ventana = { w: window.innerWidth, h: window.innerHeight }) {
  let l = { top: 0, left: 0, right: ventana.w, bottom: ventana.h };
  for (let p = el?.parentElement; p && p !== document.body && p !== document.documentElement; p = p.parentElement) {
    const e = window.getComputedStyle(p);
    if (/(auto|scroll|hidden|clip)/.test(e.overflow + e.overflowX + e.overflowY)) {
      const r = p.getBoundingClientRect();
      l = { top: Math.max(l.top, r.top), left: Math.max(l.left, r.left),
            right: Math.min(l.right, r.right), bottom: Math.min(l.bottom, r.bottom) };
    }
  }
  return l;
}

// El ancestro que hace de bloque contenedor de un position:fixed: uno con transform,
// perspective, filter o contain. Un modal de esta app lo es (su animacion deja un
// transform: scale(1)), y dentro de el "fixed" se coloca respecto al modal y no a la ventana.
export function contenedorDeFijos(el) {
  const activo = v => !!v && v !== "none";
  for (let p = el?.parentElement; p && p !== document.body && p !== document.documentElement; p = p.parentElement) {
    const e = window.getComputedStyle(p);
    if (activo(e.transform) || activo(e.perspective) || activo(e.filter) || /(paint|layout|strict|content)/.test(e.contain || "")
        || /(transform|perspective)/.test(e.willChange || "")) return p;
  }
  return null;
}

// Donde se mide un panel position:fixed: zona en la que tiene que caber y origen de sus
// coordenadas. Normalmente la ventana entera; dentro de un contenedor de fijos (un modal), ese
// contenedor, con la zona recortada por los overflow de la jerarquia.
export function contextoFijo(ancla, ventana = { w: window.innerWidth, h: window.innerHeight }) {
  const cb = contenedorDeFijos(ancla);
  if (!cb) return { limites: { top: 0, left: 0, right: ventana.w, bottom: ventana.h },
                    origen: { top: 0, left: 0, bottom: ventana.h }, scroll: { x: 0, y: 0 } };
  const r = cb.getBoundingClientRect(), e = window.getComputedStyle(cb);
  const b = k => parseFloat(e["border" + k + "Width"]) || 0;
  return { limites: limitesVisibles(ancla, ventana),
           origen: { top: r.top + b("Top"), left: r.left + b("Left"), bottom: r.bottom - b("Bottom") },
           scroll: { x: cb.scrollLeft || 0, y: cb.scrollTop || 0 } };
}

// Las coordenadas CSS del panel a partir de donde esta el boton y de la posicion elegida.
// Un panel position:fixed se saca de cualquier contenedor con scroll u overflow (un tablero con
// scroll lateral, una barra de filtros...), que lo recortaban aunque cupiera en la ventana. Si
// esta dentro de un contenedor de fijos, se resta su origen y se suma su scroll.
export function coordenadasFijas({ ancla, pos, separacion = SEPARACION, origen, scroll = { x: 0, y: 0 } }) {
  return {
    left: Math.round(ancla.left + pos.dx - origen.left + scroll.x),
    top: Math.round(ancla.bottom + separacion - origen.top + scroll.y),
    bottom: Math.round(origen.bottom - (ancla.top - separacion) - scroll.y),
  };
}

// Estilo del panel: position:fixed, pegado al boton por debajo (top) o por encima (bottom).
export function estiloPosicion(pos, coords, ancho) {
  return {
    position: "fixed",
    ...(pos.vertical === "arriba" ? { bottom: coords.bottom } : { top: coords.top }),
    left: coords.left,
    maxHeight: pos.maxAlto,
    overflowY: "auto",
    ...(ancho ? { width: ancho } : {}),
  };
}

// Uso:
//   const { estilo } = usePosicionDesplegable(abierto, anclaRef, panelRef, { altoMax: 280 });
//   {abierto && <div ref={panelRef} style={{ ...suyo, ...estilo }}>...</div>}
// `anclaRef` es el elemento que envuelve al boton; el panel se coloca respecto a EL pero con
// position:fixed, asi que no hace falta que el panel este dentro de un contenedor relative y
// ningun overflow lo recorta. El panel sigue en el mismo sitio del arbol (los clics dentro
// cuentan como dentro, y la trampa de foco de los modales lo ve).
//   separacion    lo que se deja entre el boton y el panel (negativa si se monta sobre el)
//   altoMax       tope de alto propio del panel
//   igualarAncho  el panel mide lo mismo que el boton (sugerencias de un campo de texto)
export function usePosicionDesplegable(abierto, anclaRef, panelRef,
  { preferirArriba = false, separacion = SEPARACION, altoMax = Infinity, igualarAncho = false } = {}) {
  const inicial = { vertical: preferirArriba ? "arriba" : "abajo", maxAlto: 9999, dx: 0,
                    coords: { left: 0, top: 0, bottom: 0 }, ancho: undefined };
  const [pos, setPos] = useState(inicial);

  const medir = useCallback(() => {
    const ancla = anclaRef.current, panel = panelRef.current;
    if (!ancla || !panel) return;
    const r = ancla.getBoundingClientRect();
    // El alto y el ancho NATURALES del panel: se mide sin el tope de alto que le pusimos antes.
    const antes = panel.style.maxHeight;
    panel.style.maxHeight = "none";
    const medida = { ancho: igualarAncho ? (r.right - r.left) : panel.offsetWidth, alto: panel.scrollHeight || panel.offsetHeight };
    panel.style.maxHeight = antes;
    const ctx = contextoFijo(ancla);
    const a = { top: r.top, bottom: r.bottom, left: r.left, right: r.right };
    const el = elegirPosicion({ ancla: a, panel: medida, limites: ctx.limites, preferirArriba, separacion, altoMax });
    const nueva = { ...el, coords: coordenadasFijas({ ancla: a, pos: el, separacion, origen: ctx.origen, scroll: ctx.scroll }),
                    ancho: igualarAncho ? Math.round(r.right - r.left) : undefined };
    setPos(p => (p.vertical === nueva.vertical && p.maxAlto === nueva.maxAlto && p.dx === nueva.dx && p.ancho === nueva.ancho
      && p.coords.left === nueva.coords.left && p.coords.top === nueva.coords.top && p.coords.bottom === nueva.coords.bottom ? p : nueva));
  }, [anclaRef, panelRef, preferirArriba, separacion, altoMax, igualarAncho]);

  useLayoutEffect(() => {
    if (!abierto) return undefined;
    medir();
    const rehacer = () => medir();
    window.addEventListener("resize", rehacer);
    window.addEventListener("scroll", rehacer, true);   // el scroll de cualquier contenedor, no solo el de la ventana
    return () => { window.removeEventListener("resize", rehacer); window.removeEventListener("scroll", rehacer, true); };
  }, [abierto, medir]);

  return { estilo: estiloPosicion(pos, pos.coords, pos.ancho), medir, posicion: pos };
}
